// 編集コントロール関連
import { tempTransform, editingImageIndex, editingImage, currentSelectedTemplate } from "./state.js";

// 長押し状態管理
let longPressTimeout = null;
let continuousInterval = null;
let isLongPressActive = false;
let isKeyDown = false;

// 長押しの設定値
const LONG_PRESS_DELAY = 300; // 長押し判定までの時間（ミリ秒）
const CONTINUOUS_INTERVAL = 75; // 連続実行の間隔（ミリ秒）

// 長押し状態をリセットする関数
const resetLongPressState = () => {
  if (longPressTimeout) {
    clearTimeout(longPressTimeout);
    longPressTimeout = null;
  }
  if (continuousInterval) {
    clearInterval(continuousInterval);
    continuousInterval = null;
  }
  isLongPressActive = false;
  isKeyDown = false;
};

// 連続実行を開始する関数
const startContinuousAction = (action) => {
  isLongPressActive = true;
  continuousInterval = setInterval(() => {
    action();
  }, CONTINUOUS_INTERVAL);
};

// 長押しを開始する関数
const triggerLongPress = (action) => {
  startContinuousAction(action);
};

// Coverフィットを維持するためのチェック関数
export const checkCoverFit = (popupCanvas) => {
  if (!currentSelectedTemplate || editingImageIndex === null || !editingImage) return;

  const slot = currentSelectedTemplate.photoSlots[editingImageIndex].targetSlot;
  const slotAspectRatio = slot.width / slot.height;

  // 表示用のスロットサイズ
  const maxWidth = popupCanvas.width * 0.8;
  const maxHeight = popupCanvas.height * 0.8;
  let displaySlotWidth, displaySlotHeight;

  if (slotAspectRatio > maxWidth / maxHeight) {
    displaySlotWidth = maxWidth;
    displaySlotHeight = maxWidth / slotAspectRatio;
  } else {
    displaySlotHeight = maxHeight;
    displaySlotWidth = maxHeight * slotAspectRatio;
  }

  const borderWidth = (displaySlotWidth / slot.width) * 1.5;
  const innerWidth = displaySlotWidth - borderWidth * 2;
  const innerHeight = displaySlotHeight - borderWidth * 2;

  // 画像のアスペクト比
  const imgAspectRatio = editingImage.width / editingImage.height;
  const innerAspectRatio = innerWidth / innerHeight;

  // Coverフィットでの基本サイズ（回転なしの状態）
  let baseImgWidth, baseImgHeight;
  if (imgAspectRatio > innerAspectRatio) {
    baseImgHeight = innerHeight;
    baseImgWidth = baseImgHeight * imgAspectRatio;
  } else {
    baseImgWidth = innerWidth;
    baseImgHeight = baseImgWidth / imgAspectRatio;
  }

  // 画像の回転角度（正規化して計算精度を向上）
  let normalizedRotation = tempTransform.rotation % 360;
  if (normalizedRotation < 0) normalizedRotation += 360;
  const imageRotation = (normalizedRotation * Math.PI) / 180;

  // 三角関数の値を事前計算（精度向上）
  const cosTheta = Math.cos(imageRotation);
  const sinTheta = Math.sin(imageRotation);
  const cosNegTheta = Math.cos(-imageRotation);
  const sinNegTheta = Math.sin(-imageRotation);

  // 回転を考慮した最小スケールを計算
  const frameCorners = [
    { x: -innerWidth / 2, y: -innerHeight / 2 }, // 左上
    { x: innerWidth / 2, y: -innerHeight / 2 }, // 右上
    { x: innerWidth / 2, y: innerHeight / 2 }, // 右下
    { x: -innerWidth / 2, y: innerHeight / 2 }, // 左下
  ];

  let maxRequiredScale = 1;
  frameCorners.forEach((corner, index) => {
    const rotatedCornerX = corner.x * cosNegTheta - corner.y * sinNegTheta;
    const rotatedCornerY = corner.x * sinNegTheta + corner.y * cosNegTheta;

    const requiredScaleX = Math.abs(rotatedCornerX) / (baseImgWidth / 2);
    const requiredScaleY = Math.abs(rotatedCornerY) / (baseImgHeight / 2);
    const requiredScale = Math.max(requiredScaleX, requiredScaleY);

    maxRequiredScale = Math.max(maxRequiredScale, requiredScale);

    console.log(`[checkCoverFit] Corner ${index}: (${corner.x.toFixed(1)}, ${corner.y.toFixed(1)}) -> rotated: (${rotatedCornerX.toFixed(3)}, ${rotatedCornerY.toFixed(3)}) -> required scale: ${requiredScale.toFixed(4)}`);
  });

  console.log(`[checkCoverFit] Rotation: ${normalizedRotation}°, Current scale: ${tempTransform.scale.toFixed(4)}, Required scale: ${maxRequiredScale.toFixed(4)}`);

  // 現在のスケールが必要最小値を下回っている場合は調整（小さなマージンを追加）
  const scaleEpsilon = 0.0001; // スケールの精度マージン
  if (tempTransform.scale < maxRequiredScale + scaleEpsilon) {
    console.log(`[checkCoverFit] Adjusting scale from ${tempTransform.scale.toFixed(4)} to ${(maxRequiredScale + scaleEpsilon).toFixed(4)}`);
    tempTransform.scale = maxRequiredScale + scaleEpsilon;
  }

  // オフセットの制限：現在のオフセット位置で余白ができないかをチェック
  const imgX = -baseImgWidth / 2 + tempTransform.offsetX;
  const imgY = -baseImgHeight / 2 + tempTransform.offsetY;

  // 各方向での最大許可オフセットを計算
  let minAllowedOffsetX = -Infinity;
  let maxAllowedOffsetX = Infinity;
  let minAllowedOffsetY = -Infinity;
  let maxAllowedOffsetY = Infinity;

  for (const frameCorner of frameCorners) {
    // 1. 枠のコーナーを回転前の座標系に変換（逆回転）
    const unrotatedX = frameCorner.x * cosNegTheta - frameCorner.y * sinNegTheta;
    const unrotatedY = frameCorner.x * sinNegTheta + frameCorner.y * cosNegTheta;

    // 2. スケールを考慮した座標系に変換（逆スケール）
    const unscaledX = unrotatedX / tempTransform.scale;
    const unscaledY = unrotatedY / tempTransform.scale;

    // 3. このコーナーが画像内に収まるために必要なオフセット範囲を計算
    const minOffsetXForThisCorner = unscaledX - baseImgWidth / 2;
    const maxOffsetXForThisCorner = unscaledX + baseImgWidth / 2;
    const minOffsetYForThisCorner = unscaledY - baseImgHeight / 2;
    const maxOffsetYForThisCorner = unscaledY + baseImgHeight / 2;

    // より厳しい制約を採用（全てのコーナーが画像内に収まる必要がある）
    minAllowedOffsetX = Math.max(minAllowedOffsetX, minOffsetXForThisCorner);
    maxAllowedOffsetX = Math.min(maxAllowedOffsetX, maxOffsetXForThisCorner);
    minAllowedOffsetY = Math.max(minAllowedOffsetY, minOffsetYForThisCorner);
    maxAllowedOffsetY = Math.min(maxAllowedOffsetY, maxOffsetYForThisCorner);

    console.log(`[checkCoverFit] Corner (${frameCorner.x.toFixed(1)}, ${frameCorner.y.toFixed(1)}) -> unrotated (${unrotatedX.toFixed(3)}, ${unrotatedY.toFixed(3)}) -> unscaled (${unscaledX.toFixed(3)}, ${unscaledY.toFixed(3)})`);
    console.log(`[checkCoverFit] Required offset ranges: X[${minOffsetXForThisCorner.toFixed(3)}, ${maxOffsetXForThisCorner.toFixed(3)}], Y[${minOffsetYForThisCorner.toFixed(3)}, ${maxOffsetYForThisCorner.toFixed(3)}]`);
  }

  console.log(`[checkCoverFit] Final allowed ranges: X[${minAllowedOffsetX.toFixed(3)}, ${maxAllowedOffsetX.toFixed(3)}], Y[${minAllowedOffsetY.toFixed(3)}, ${maxAllowedOffsetY.toFixed(3)}]`);
  console.log(`[checkCoverFit] Current offset: (${tempTransform.offsetX.toFixed(3)}, ${tempTransform.offsetY.toFixed(3)})`);

  // 数値精度を考慮した小さなマージンを追加（浮動小数点誤差対策）
  const epsilon = 0.01; // 1/100ピクセルのマージン

  // オフセットを許可範囲内に調整（マージンを考慮）
  const adjustedOffsetX = Math.max(minAllowedOffsetX + epsilon, Math.min(maxAllowedOffsetX - epsilon, tempTransform.offsetX));
  const adjustedOffsetY = Math.max(minAllowedOffsetY + epsilon, Math.min(maxAllowedOffsetY - epsilon, tempTransform.offsetY));

  // 調整が必要かチェック
  if (Math.abs(adjustedOffsetX - tempTransform.offsetX) > 0.001 || Math.abs(adjustedOffsetY - tempTransform.offsetY) > 0.001) {
    console.log(`[checkCoverFit] Adjusting offset from (${tempTransform.offsetX.toFixed(3)}, ${tempTransform.offsetY.toFixed(3)}) to (${adjustedOffsetX.toFixed(3)}, ${adjustedOffsetY.toFixed(3)})`);
    tempTransform.offsetX = adjustedOffsetX;
    tempTransform.offsetY = adjustedOffsetY;
  }
};

// 移動可能かどうかをチェックする関数
export const canMoveToPosition = (newOffsetX, newOffsetY, popupCanvas) => {
  if (!currentSelectedTemplate || editingImageIndex === null || !editingImage) return true;

  const slot = currentSelectedTemplate.photoSlots[editingImageIndex].targetSlot;
  const slotAspectRatio = slot.width / slot.height;

  // 表示用のスロットサイズ
  const maxWidth = popupCanvas.width * 0.8;
  const maxHeight = popupCanvas.height * 0.8;
  let displaySlotWidth, displaySlotHeight;

  if (slotAspectRatio > maxWidth / maxHeight) {
    displaySlotWidth = maxWidth;
    displaySlotHeight = maxWidth / slotAspectRatio;
  } else {
    displaySlotHeight = maxHeight;
    displaySlotWidth = maxHeight * slotAspectRatio;
  }

  const borderWidth = (displaySlotWidth / slot.width) * 1.5;
  const innerWidth = displaySlotWidth - borderWidth * 2;
  const innerHeight = displaySlotHeight - borderWidth * 2;

  // 画像のアスペクト比とCoverフィット計算
  const imgAspectRatio = editingImage.width / editingImage.height;
  const innerAspectRatio = innerWidth / innerHeight;

  let baseImgWidth, baseImgHeight;
  if (imgAspectRatio > innerAspectRatio) {
    baseImgHeight = innerHeight;
    baseImgWidth = baseImgHeight * imgAspectRatio;
  } else {
    baseImgWidth = innerWidth;
    baseImgHeight = baseImgWidth / imgAspectRatio;
  }

  // 画像の回転角度（正規化して計算精度を向上）
  let normalizedRotation = tempTransform.rotation % 360;
  if (normalizedRotation < 0) normalizedRotation += 360;
  const imageRotation = (normalizedRotation * Math.PI) / 180;

  // 三角関数の値を事前計算（精度向上）
  const cosTheta = Math.cos(imageRotation);
  const sinTheta = Math.sin(imageRotation);
  const cosNegTheta = Math.cos(-imageRotation);
  const sinNegTheta = Math.sin(-imageRotation);

  // 回転を考慮した最小スケールを計算
  const frameCorners = [
    { x: -innerWidth / 2, y: -innerHeight / 2 }, // 左上
    { x: innerWidth / 2, y: -innerHeight / 2 }, // 右上
    { x: innerWidth / 2, y: innerHeight / 2 }, // 右下
    { x: -innerWidth / 2, y: innerHeight / 2 }, // 左下
  ];

  let maxRequiredScale = 1;
  frameCorners.forEach((corner) => {
    const rotatedCornerX = corner.x * cosNegTheta - corner.y * sinNegTheta;
    const rotatedCornerY = corner.x * sinNegTheta + corner.y * cosNegTheta;

    const requiredScaleX = Math.abs(rotatedCornerX) / (baseImgWidth / 2);
    const requiredScaleY = Math.abs(rotatedCornerY) / (baseImgHeight / 2);
    const requiredScale = Math.max(requiredScaleX, requiredScaleY);

    maxRequiredScale = Math.max(maxRequiredScale, requiredScale);
  });

  // 実際に使用されるスケール（ユーザー設定と最小要求の最大値）
  const scaleEpsilon = 0.0001; // スケールの精度マージン
  const effectiveScale = Math.max(tempTransform.scale, maxRequiredScale + scaleEpsilon);

  // 描画処理と同じ計算：移動後の画像の描画位置
  const imgX = -baseImgWidth / 2 + newOffsetX;
  const imgY = -baseImgHeight / 2 + newOffsetY;

  // 枠の四隅が全て画像内に収まるかチェック
  // 描画処理の逆変換：rotate → scale → drawImage の逆順で変換
  for (const frameCorner of frameCorners) {
    // 1. 枠のコーナーを回転前の座標系に変換（逆回転）
    const unrotatedX = frameCorner.x * cosNegTheta - frameCorner.y * sinNegTheta;
    const unrotatedY = frameCorner.x * sinNegTheta + frameCorner.y * cosNegTheta;

    // 2. スケールを考慮した座標系に変換（逆スケール）
    const unscaledX = unrotatedX / effectiveScale;
    const unscaledY = unrotatedY / effectiveScale;

    // 3. 画像の描画位置からの相対位置を計算
    const relativeX = unscaledX - imgX;
    const relativeY = unscaledY - imgY;

    // 4. 画像の境界内かチェック（小さなマージンを考慮）
    const epsilon = 0.01; // 1/100ピクセルのマージン
    if (relativeX < -epsilon || relativeX > baseImgWidth + epsilon || relativeY < -epsilon || relativeY > baseImgHeight + epsilon) {
      console.log(`[canMoveToPosition] Frame corner (${frameCorner.x.toFixed(1)}, ${frameCorner.y.toFixed(1)}) would be outside image at offset (${newOffsetX.toFixed(2)}, ${newOffsetY.toFixed(2)})`);
      console.log(`[canMoveToPosition] Transformed coords: unrotated(${unrotatedX.toFixed(3)}, ${unrotatedY.toFixed(3)}), unscaled(${unscaledX.toFixed(3)}, ${unscaledY.toFixed(3)}), relative(${relativeX.toFixed(3)}, ${relativeY.toFixed(3)})`);
      console.log(`[canMoveToPosition] Image bounds: 0-${baseImgWidth.toFixed(1)}, 0-${baseImgHeight.toFixed(1)}, Scale: ${effectiveScale.toFixed(4)}`);
      return false;
    }
  }

  console.log(`[canMoveToPosition] Move to (${newOffsetX.toFixed(2)}, ${newOffsetY.toFixed(2)}) is allowed - all frame corners within image bounds`);
  return true;
};

// Coverフィット制限の警告メッセージを表示する関数
export const showCoverFitWarning = () => {
  // 既存の警告メッセージを削除
  const existingWarning = document.getElementById("coverFitWarning");
  if (existingWarning) {
    existingWarning.remove();
  }

  // 新しい警告メッセージを作成
  const warningDiv = document.createElement("div");
  warningDiv.id = "coverFitWarning";
  warningDiv.style.cssText = `
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    color: #856404;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 1001;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  `;
  warningDiv.textContent = "写真に余白ができないよう自動調整されました";

  // モーダルコンテンツに追加
  const modalContent = document.querySelector(".album-modal-content");
  if (modalContent) {
    modalContent.appendChild(warningDiv);

    // 3秒後に自動で消去
    setTimeout(() => {
      if (warningDiv && warningDiv.parentNode) {
        warningDiv.remove();
      }
    }, 3000);
  }
};

// 移動できない場合に警告メッセージを表示する関数
export const showMoveBlockedWarning = (buttonText) => {
  // 既存の警告メッセージを削除
  const existingWarning = document.getElementById("moveBlockedWarning");
  if (existingWarning) {
    existingWarning.remove();
  }

  // 新しい警告メッセージを作成
  const warningDiv = document.createElement("div");
  warningDiv.id = "moveBlockedWarning";
  warningDiv.style.cssText = `
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 1001;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  `;
  warningDiv.textContent = `これ以上この方向には移動できません`;

  // モーダルコンテンツに追加
  const modalContent = document.querySelector(".album-modal-content");
  if (modalContent) {
    modalContent.appendChild(warningDiv);

    // 3秒後に自動で消去
    setTimeout(() => {
      if (warningDiv && warningDiv.parentNode) {
        warningDiv.remove();
      }
    }, 3000);
  }
};

// 編集コントロールボタンの設定
export const setupEditControls = (popupCanvas) => {
  const controlButtons = document.querySelectorAll(".album-control-button");

  controlButtons.forEach((button) => {
    // 画像ボタンの場合はalt属性から操作を判定、テキストボタンの場合はtextContentを使用
    const img = button.querySelector("img");
    const buttonAction = img ? img.alt : button.textContent;

    // 各ボタンの操作を定義
    const performAction = () => {
      if (!tempTransform || !popupCanvas.drawEditingImage) return;

      // 操作前の状態を保存（制限に引っかかった場合の復元用）
      const previousTransform = { ...tempTransform };

      switch (buttonAction) {
        case "拡大":
        case "拡大 (+)":
          tempTransform.scale = Math.min(tempTransform.scale * 1.01, 3); // 最大3倍
          break;
        case "縮小":
        case "縮小 (-)":
          tempTransform.scale = Math.max(tempTransform.scale * 0.99, 0.5); // 最小0.5倍
          break;
        case "左回転":
          tempTransform.rotation -= 0.5;
          break;
        case "右回転":
          tempTransform.rotation += 0.5;
          break;
        case "上へ":
        case "下へ":
        case "左へ":
        case "右へ":
          // 回転角度に応じた移動方向の計算
          const moveDistance = 1; // 1pxずつ移動
          const rotationRad = (tempTransform.rotation * Math.PI) / 180;

          // 各方向のベクトル（回転前の座標系）
          let moveX = 0,
            moveY = 0;

          switch (buttonAction) {
            case "上へ":
              moveX = 0;
              moveY = -moveDistance; // 上方向（Y軸負方向）
              break;
            case "下へ":
              moveX = 0;
              moveY = moveDistance; // 下方向（Y軸正方向）
              break;
            case "左へ":
              moveX = -moveDistance; // 左方向（X軸負方向）
              moveY = 0;
              break;
            case "右へ":
              moveX = moveDistance; // 右方向（X軸正方向）
              moveY = 0;
              break;
          }

          // 回転角度に応じて移動ベクトルを回転
          const cos = Math.cos(-rotationRad); // 逆回転
          const sin = Math.sin(-rotationRad); // 逆回転

          const rotatedMoveX = moveX * cos - moveY * sin;
          const rotatedMoveY = moveX * sin + moveY * cos;

          // 移動後の位置を仮計算
          const newOffsetX = tempTransform.offsetX + rotatedMoveX;
          const newOffsetY = tempTransform.offsetY + rotatedMoveY;

          // 移動可能かどうかをチェック
          if (canMoveToPosition(newOffsetX, newOffsetY, popupCanvas)) {
            // 移動可能な場合のみ実際の移動を適用
            tempTransform.offsetX = newOffsetX;
            tempTransform.offsetY = newOffsetY;

            console.log(`[Move] Button: ${buttonAction}, Rotation: ${tempTransform.rotation}°, Original move: (${moveX}, ${moveY}), Rotated move: (${rotatedMoveX.toFixed(2)}, ${rotatedMoveY.toFixed(2)})`);
          } else {
            // 移動できない場合は警告メッセージを表示
            showMoveBlockedWarning(buttonAction);
            console.log(`[Move] Blocked: ${buttonAction} - would create gaps`);
          }
          break;
      }

      // Coverフィットのチェックを実行
      if (popupCanvas.checkCoverFit) {
        // 移動操作以外の場合のみcheckCoverFitを実行
        if (buttonAction !== "上へ" && buttonAction !== "下へ" && buttonAction !== "左へ" && buttonAction !== "右へ") {
          checkCoverFit(popupCanvas);
        }
      }

      // 制限により調整された場合の警告表示
      if (currentSelectedTemplate && editingImageIndex !== null) {
        const scaleChanged = Math.abs(tempTransform.scale - previousTransform.scale) > 0.001;
        const rotationChanged = Math.abs(tempTransform.rotation - previousTransform.rotation) > 0.001;
        const offsetXChanged = Math.abs(tempTransform.offsetX - previousTransform.offsetX) > 0.1;
        const offsetYChanged = Math.abs(tempTransform.offsetY - previousTransform.offsetY) > 0.1;

        // 回転操作でスケールが自動調整された場合
        if ((buttonAction === "左回転" || buttonAction === "右回転") && scaleChanged) {
          showCoverFitWarning();
        }
        // 縮小操作が制限された場合（スケールが期待値より大きい）
        else if ((buttonAction === "縮小" || buttonAction === "縮小 (-)") && tempTransform.scale > previousTransform.scale * 0.99) {
          showCoverFitWarning();
        }
        // 移動操作が制限された場合（オフセットが変化しなかった場合）
        else if ((buttonAction === "上へ" || buttonAction === "下へ" || buttonAction === "左へ" || buttonAction === "右へ") && !offsetXChanged && !offsetYChanged) {
          showCoverFitWarning();
        }
      }

      // 再描画
      popupCanvas.drawEditingImage();
    };

    // ポインターダウン（マウス・タッチ開始）
    const handlePointerDown = (e) => {
      e.preventDefault();

      // 既存の状態をリセット
      resetLongPressState();

      // 長押し判定のタイマーを開始
      longPressTimeout = setTimeout(() => {
        triggerLongPress(performAction);
      }, LONG_PRESS_DELAY);
    };

    // ポインターアップ（マウス・タッチ終了）
    const handlePointerUp = (e) => {
      e.preventDefault();

      // 長押し中でなければ単発実行
      if (!isLongPressActive) {
        performAction();
      }

      // 状態をリセット
      resetLongPressState();
    };

    // ポインターリーブ（ボタンから離れた場合）
    const handlePointerLeave = (e) => {
      e.preventDefault();

      // 状態をリセットするのみ（単発実行はしない）
      resetLongPressState();
    };

    // キーダウン（キーボード押下）
    const handleKeyDown = (e) => {
      // EnterキーまたはSpaceキーのみ対応
      if (e.key !== "Enter" && e.key !== " ") return;

      e.preventDefault();

      // 既にキーが押されている場合は何もしない（リピート防止）
      if (isKeyDown) return;

      isKeyDown = true;

      // 長押し判定のタイマーを開始
      longPressTimeout = setTimeout(() => {
        triggerLongPress(performAction);
      }, LONG_PRESS_DELAY);
    };

    // キーアップ（キーボード離す）
    const handleKeyUp = (e) => {
      // EnterキーまたはSpaceキーのみ対応
      if (e.key !== "Enter" && e.key !== " ") return;

      e.preventDefault();

      // 長押し中でなければ単発実行
      if (!isLongPressActive && isKeyDown) {
        performAction();
      }

      // 状態をリセット
      resetLongPressState();
    };

    // イベントリスナーを設定
    // ポインターイベント（マウス・タッチ統合）
    button.addEventListener("pointerdown", handlePointerDown);
    button.addEventListener("pointerup", handlePointerUp);
    button.addEventListener("pointerleave", handlePointerLeave);
    button.addEventListener("pointercancel", handlePointerUp);

    // キーボードイベント
    button.addEventListener("keydown", handleKeyDown);
    button.addEventListener("keyup", handleKeyUp);

    // フォーカスが外れた場合の処理
    button.addEventListener("blur", () => {
      resetLongPressState();
    });

    // タッチ操作での選択防止（CSS user-select: none の代替）
    button.style.userSelect = "none";
    button.style.webkitUserSelect = "none";
    button.style.webkitTouchCallout = "none";
  });
};
